import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import {
    ApplicationRoles,
    DateHelpers,
    EdlFeatureFlags,
    NavigationFilterContext,
    NavigationLinkType,
    NavigationMapProvider,
    NavigationNode,
    NavigationNodeConfig,
    NavigationSection,
    PortalHelpers,
    PortalSettings,
    SessionService,
    User,
    exactStateMatch,
    filterNavigationNodes,
    stateMatch
} from '@edl-development/edl-common';
import { LastWeekSchedule } from 'src/app/model/last-week-schedule.model';
import { ModalService } from 'src/app/modals/modal.service';
import { PortalHelpersService } from 'src/app/services/portal-helpers/portal-helpers.service';
import { ComplyVisibility } from './models/comply-visibility.model';

@Injectable()
export class ComplyNavigationMapProvider implements NavigationMapProvider {
    private datePipe = new DatePipe('en-US');

    constructor(
        private sessionService: SessionService,
        private portalHelpersService: PortalHelpersService,
        private modalService: ModalService
    ) { }

    /** Builds and filters the full navigation map for the current user and portal context. */
    getNavigationMap(context: NavigationFilterContext): NavigationNode[] {
        const user = this.sessionService.user;
        const portalSettings = this.sessionService.PortalSettings;
        const config = this.buildNavigationConfig(user, portalSettings);
        const isVisible = this.createVisibilityPredicate(user, portalSettings);
        return filterNavigationNodes(config, isVisible, context);
    }

    /**
     * Creates a predicate that evaluates ComplyVisibility rules against the current
     * user's role, portal feature flags, and optional condition callback.
     */
    private createVisibilityPredicate(user: User, portalSettings: PortalSettings): (v: ComplyVisibility | undefined) => boolean {
        return (v) => {
            if (!v) return true;

            if (v.roles?.length && !v.roles.includes(user.AccessRoleID)) {
                return false;
            }

            if (v.featureFlags?.length) {
                if (!v.featureFlags.every(flag => PortalHelpers.isFeatureFlagEnabled(portalSettings, flag))) {
                    return false;
                }
            }

            if (v.condition && !v.condition(user, portalSettings)) {
                return false;
            }

            return true;
        };
    }

    /** Combines sidebar and header navigation configs into a single list. */
    private buildNavigationConfig(user: User, portalSettings: PortalSettings): NavigationNodeConfig<ComplyVisibility>[] {
        return [
            ...this.getSidebarConfig(),
            ...this.getHeaderConfig(user, portalSettings)
        ];
    }

    /** Returns the sidebar navigation tree: main sections with role/portal/feature-flag visibility. */
    private getSidebarConfig(): NavigationNodeConfig<ComplyVisibility>[] {
        return [
            {
                id: 'my-items',
                linkName: 'My Items',
                icon: 'fas fa-check',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.my-items',
                    uiOptions: { reload: true }
                },
                section: [NavigationSection.Sidebar]
            },
            {
                id: 'all-users-progress',
                linkName: "All Users' Progress",
                icon: 'fa fa-check-circle',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.all-users-progress.user-compliance.view',
                    uiParams: { page: 1, status: 3, searchBy: '' },
                    uiOptions: { reload: true }
                },
                activeStateRoot: 'authenticated.all-users-progress',
                visibility: {
                    roles: [ApplicationRoles.ADMIN, ApplicationRoles.EDLADMIN, ApplicationRoles.SUPERVISOR]
                },
                section: [NavigationSection.Sidebar]
            },
            {
                id: 'compliance-items',
                linkName: 'Compliance Items',
                icon: 'fas fa-file',
                link: { type: NavigationLinkType.None },
                visibility: {
                    roles: [ApplicationRoles.ADMIN, ApplicationRoles.EDLADMIN, ApplicationRoles.SUPERVISOR, ApplicationRoles.LIMITED_SUPERVISOR],
                    condition: (_u, s) => this.portalHelpersService.canViewComplianceItems(s)
                },
                activeStateRoot: 'authenticated.compliance-items',
                section: [NavigationSection.Sidebar],
                children: [
                    {
                        id: 'compliance-items-my-supervision',
                        linkName: 'My Supervision Items',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.compliance-items.my-supervision-items.all',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        }
                    },
                    {
                        id: 'compliance-items-all',
                        linkName: 'All Items',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.compliance-items.view',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            roles: [ApplicationRoles.ADMIN, ApplicationRoles.EDLADMIN, ApplicationRoles.SUPERVISOR]
                        }
                    },
                    {
                        id: 'compliance-items-templates',
                        linkName: 'Templates',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.compliance-items.templates',
                            uiParams: { page: 1, status: 3, searchBy: '' },
                            uiOptions: { reload: true }
                        }
                    },
                    {
                        id: 'compliance-items-add',
                        linkName: 'Add Item',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.compliance-items.add',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        }
                    }
                ]
            },
            {
                id: 'requests',
                linkName: 'Requests',
                icon: 'fas fa-file-alt',
                link: { type: NavigationLinkType.None },
                visibility: {
                    roles: [ApplicationRoles.ADMIN, ApplicationRoles.EDLADMIN, ApplicationRoles.SUPERVISOR, ApplicationRoles.LIMITED_SUPERVISOR]
                },
                activeStateRoot: 'authenticated.requests',
                section: [NavigationSection.Sidebar],
                children: [
                    {
                        id: 'requests-my-supervision',
                        linkName: 'My Supervision Requests',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.requests.sm.search',
                            uiParams: { type: 'myrequests', page: 1 },
                            uiOptions: { reload: true }
                        },
                        isActive: stateMatch([
                            { state: 'authenticated.requests.sm.search', params: { type: 'myrequests' } }
                        ])
                    },
                    {
                        id: 'requests-all',
                        linkName: 'All Requests',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.requests.sm.search',
                            uiParams: { type: 'all', page: 1 },
                            uiOptions: { reload: true }
                        },
                        isActive: stateMatch([
                            { state: 'authenticated.requests.sm.search', params: { type: 'all' } }
                        ]),
                        visibility: {
                            roles: [ApplicationRoles.ADMIN, ApplicationRoles.EDLADMIN, ApplicationRoles.SUPERVISOR]
                        }
                    },
                    {
                        id: 'requests-templates',
                        linkName: 'Templates',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.requests.templates',
                            uiParams: { status: 5, type: 'all', page: 1 }
                        }
                    },
                    {
                        id: 'requests-add',
                        linkName: 'Add Request',
                        link: { type: NavigationLinkType.State, uiSref: 'authenticated.requests.create' },
                        mobileHidden: true
                    }
                ]
            },
            {
                id: 'questionnaires',
                linkName: 'Questionnaires',
                icon: 'fas fa-question',
                link: { type: NavigationLinkType.None },
                visibility: {
                    roles: [ApplicationRoles.ADMIN, ApplicationRoles.EDLADMIN, ApplicationRoles.SUPERVISOR, ApplicationRoles.LIMITED_SUPERVISOR]
                },
                activeStateRoot: 'authenticated.questionnaires',
                section: [NavigationSection.Sidebar],
                children: [
                    {
                        id: 'questionnaires-all',
                        linkName: 'All Questionnaires',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.questionnaires.view',
                            uiParams: { page: 1, status: 3 },
                            uiOptions: { reload: true }
                        },
                        isActive: stateMatch(
                            [{ state: 'authenticated.questionnaires'}],
                            [
                                { state: 'authenticated.questionnaires.view.search', params: { status: 3 } },
                                { state: 'authenticated.questionnaires.add' }
                            ]
                        )
                    },
                    {
                        id: 'questionnaires-templates',
                        linkName: 'Templates',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.questionnaires.view.search',
                            uiParams: { page: 1, status: 3 },
                            uiOptions: { reload: true }
                        },
                        isActive: stateMatch([{ state: 'authenticated.questionnaires.view.search', params: { status: 3 } }])
                    },
                    {
                        id: 'questionnaires-add',
                        linkName: 'Add Questionnaire',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.questionnaires.add',
                            uiParams: { page: 1, status: 3 },
                            uiOptions: { reload: true }
                        }
                    }
                ]
            },
            {
                id: 'user-management',
                linkName: 'User Management',
                icon: 'fas fa-user',
                link: { type: NavigationLinkType.None },
                visibility: {
                    roles: [ApplicationRoles.ADMIN, ApplicationRoles.EDLADMIN]
                },
                activeStateRoot: 'authenticated.users',
                section: [NavigationSection.Sidebar],
                children: [
                    {
                        id: 'users-all',
                        linkName: 'All Users',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.users.view',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        isActive: stateMatch([{ state: 'authenticated.users' }], [{ state: 'authenticated.users.onboard' }, { state: 'authenticated.users.bulkupdate' }])
                    },
                    {
                        id: 'users-onboard',
                        linkName: 'Onboard Users',
                        link: { type: NavigationLinkType.State, uiSref: 'authenticated.users.onboard' },
                        mobileHidden: true
                    },
                    {
                        id: 'users-bulk-update',
                        linkName: 'Bulk Update Users',
                        link: { type: NavigationLinkType.State, uiSref: 'authenticated.users.bulkupdate' }
                    },
                    {
                        id: 'groups-management',
                        linkName: 'Group Management',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.groups.view',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        isActive: stateMatch([{ state: 'authenticated.groups' }], [{ state: 'authenticated.groups.bulkupdate' }]),
                        mobileHidden: true
                    },
                    {
                        id: 'groups-bulk-update',
                        linkName: 'Bulk Update Groups',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.groups.bulkupdate',
                            uiParams: { page: 1, building: -1, role: -1, group: -1, searchBy: '' },
                            uiOptions: { reload: true }
                        },
                        mobileHidden: true
                    },
                    {
                        id: 'volunteer-areas',
                        linkName: 'Volunteer Areas',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.volunteerAreas.view',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        activeStateRoot: 'authenticated.volunteerAreas',
                        visibility: {
                            featureFlags: [EdlFeatureFlags.VolunteersEnabled]
                        }
                    }
                ]
            },
            {
                id: 'reporting',
                linkName: 'Reporting',
                icon: 'far fa-chart-bar',
                link: { type: NavigationLinkType.State, uiSref: 'authenticated.reports.view', uiOptions: { reload: true } },
                visibility: {
                    condition: (u, s) => this.portalHelpersService.canViewReports(s, u)
                },
                activeStateRoot: 'authenticated.reports',
                mobileHidden: true,
                section: [NavigationSection.Sidebar]
            },
            {
                id: 'administration',
                linkName: 'Administration',
                icon: 'fas fa-cog',
                link: { type: NavigationLinkType.None },
                visibility: {
                    roles: [ApplicationRoles.ADMIN, ApplicationRoles.EDLADMIN]
                },
                activeStateRoot: 'authenticated.administration',
                section: [NavigationSection.Sidebar],
                children: [
                    {
                        id: 'admin-base',
                        linkName: 'Welcome Page',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration',
                            uiOptions: { reload: true }
                        },
                        isActive: exactStateMatch('authenticated.administration'),
                        visibility: {
                            roles: [ApplicationRoles.EDLADMIN]
                        }
                    },
                    {
                        id: 'admin-auto-assignment-rules',
                        linkName: 'Auto Assignment Rules',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.auto-assignment.rules',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            roles: [ApplicationRoles.EDLADMIN],
                            featureFlags: [EdlFeatureFlags.AutoAssignmentEnabled]
                        }
                    },
                    {
                        id: 'admin-auto-assignment-logs',
                        linkName: 'Auto Assignment Logs',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.auto-assignment.logs',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            roles: [ApplicationRoles.EDLADMIN],
                            featureFlags: [EdlFeatureFlags.AutoAssignmentEnabled]
                        }
                    },
                    {
                        id: 'admin-portal-settings',
                        linkName: 'Portal Settings',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.settings',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            roles: [ApplicationRoles.EDLADMIN]
                        }
                    },
                    {
                        id: 'admin-email-content',
                        linkName: 'Email Content',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.emailcontent',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            roles: [ApplicationRoles.EDLADMIN]
                        }
                    },
                    {
                        id: 'admin-alert-content',
                        linkName: 'Alert Content',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.alertcontent',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            roles: [ApplicationRoles.EDLADMIN]
                        }
                    },
                    {
                        id: 'admin-item-packets',
                        linkName: 'Item Packets',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.item-packet.list',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        activeStateRoot: 'authenticated.administration.item-packet',
                        visibility: {
                            condition: (_u, s) => this.portalHelpersService.canViewItemPackets(s)
                        }
                    },
                    {
                        id: 'admin-template-types',
                        linkName: 'Template Types',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.edl-template-types',
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            roles: [ApplicationRoles.EDLADMIN]
                        }
                    },
                    {
                        id: 'admin-token-management',
                        linkName: 'Token Management',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.token-list',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        isActive: stateMatch([
                            { state: 'authenticated.administration.token-list' },
                            { state: 'authenticated.administration.token-add' },
                            { state: 'authenticated.administration.token-edit' }
                        ])
                    }
                ]
            }
        ];
    }

    /** Returns header navigation items: messages, support, and profile links. */
    private getHeaderConfig(user: User, portalSettings: PortalSettings): NavigationNodeConfig<ComplyVisibility>[] {
        const messagesParams = this.generateMessagesParams();

        return [
            {
                id: 'messages',
                linkName: 'Messages',
                icon: 'fas fa-envelope',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.messages.my-messages',
                    uiParams: messagesParams
                },
                section: [NavigationSection.Header]
            },
            {
                id: 'support',
                linkName: 'Support',
                icon: 'fas fa-question-circle',
                link: this.portalHelpersService.canViewVolunteerAreas(portalSettings)
                    ? { type: NavigationLinkType.Action, action: () => this.modalService.info('Support', 'Please contact your district if you have any questions.') }
                    : { type: NavigationLinkType.External, href: 'https://edulink.freshdesk.com/support/home', target: '_blank' },
                section: [NavigationSection.Header]
            },
            {
                id: 'update-profile',
                linkName: 'Update Profile',
                icon: 'fas fa-user-edit',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.users.user.edit',
                    uiParams: { userId: user?.UserID }
                },
                section: [NavigationSection.Header]
            }
        ];
    }

    /** Generates date-range params for the messages link based on last week's schedule. */
    private generateMessagesParams(): { page: number, start: string, end: string } {
        const lastWeekSchedule = new LastWeekSchedule();
        return {
            page: 1,
            start: this.datePipe.transform(DateHelpers.shortDateStringToDate(lastWeekSchedule.StartDate), 'MM/dd/yyyy'),
            end: this.datePipe.transform(DateHelpers.shortDateStringToDate(lastWeekSchedule.EndDate), 'MM/dd/yyyy'),
        };
    }
}
